import { useRef, useState } from "react";
import { UploadCloud, FileText, Trash2, CheckCircle2, Clock, Plus, Eye, Send, X } from "lucide-react";
import { useRegistration } from "@/context/RegistrationContext";
import { deleteFile } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { FilePreviewDialog } from "./FilePreviewDialog";

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// Finds the reason for the most recent "reupload requested" event that targeted
// this exact document key — the app-level lastActionReason is shared across all
// docs, so it can show the wrong reason once more than one doc has been flagged.
export const reuploadReasonFor = (history, key) => {
  const entry = (history || []).find((h) => h?.detail && h.detail[key] === "reupload");
  return entry?.reason || null;
};

const readAsDataURL = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

export const UploadCard = ({ doc, files = [], onChange, locked = false, onLockedClick }) => {
  const { state } = useRegistration();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(null);
  // Files picked but not yet confirmed — lets the candidate cross-check what
  // they selected (right file? right document?) before it actually uploads.
  const [pending, setPending] = useState([]);
  const [pendingPreviewIdx, setPendingPreviewIdx] = useState(null);

  const stageFiles = async (fileList) => {
    let arr = Array.from(fileList);
    if (!arr.length) return;
    // The `accept` attribute only filters the native file picker, not
    // drag-and-drop — enforce PDF-only doc types (e.g. anti_ragging) here too.
    if (doc.accept === ".pdf") {
      const rejected = arr.filter((f) => f.type !== "application/pdf" && !/\.pdf$/i.test(f.name));
      if (rejected.length) {
        toast.error("Only PDF files are accepted for this document");
        arr = arr.filter((f) => !rejected.includes(f));
      }
      if (!arr.length) return;
    }
    const processed = await Promise.all(
      arr.map(async (f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
        dataUrl: await readAsDataURL(f),
      }))
    );
    // Single-file docs: a fresh pick replaces whatever was staged before.
    setPending((prev) => (doc.multi ? [...prev, ...processed] : [processed[0]]));
  };

  const removePending = (idx) => setPending((prev) => prev.filter((_, i) => i !== idx));

  const confirmUpload = async () => {
    if (!pending.length) return;
    setUploading(true);
    setProgress(0);
    // simulate progress — the real upload to storage happens in the
    // background once onChange hands these off to registration state.
    for (let p = 10; p <= 90; p += 20) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 80));
      setProgress(p);
    }
    setProgress(100);
    const withTimestamp = pending.map((f) => ({ ...f, uploadedAt: new Date().toISOString() }));
    const next = doc.multi ? [...(files || []), ...withTimestamp] : [withTimestamp[0]];
    onChange(next);
    setPending([]);
    setTimeout(() => {
      setUploading(false);
      setProgress(0);
    }, 400);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (locked) return onLockedClick?.();
    if (e.dataTransfer?.files) stageFiles(e.dataTransfer.files);
  };

  const removeAt = async (idx) => {
    const target = files[idx];
    // Optimistic local removal first
    const next = files.filter((_, i) => i !== idx);
    onChange(next);
    // Then remove from Supabase Storage + registration_files if it was already uploaded
    if (target?.file_path && state.applicationNumber) {
      await deleteFile({
        applicationNumber: state.applicationNumber,
        category: "document",
        key: doc.id,
        filePath: target.file_path,
      });
    }
  };

  const hasFiles = files && files.length > 0;
  const hasPending = pending.length > 0;
  const docStatus = state.docStatuses?.[doc.id];
  const needsReupload = docStatus === "reupload";
  const reuploadReason = needsReupload ? reuploadReasonFor(state.history, doc.id) : null;

  return (
    <motion.div
      layout
      className={`mu-card p-5 flex flex-col gap-4 ${needsReupload ? "border-orange-300 ring-2 ring-orange-100" : ""} ${locked ? "opacity-60" : ""}`}
      data-testid={`upload-card-${doc.id}`}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-[#111]">{doc.label}</h3>
            {doc.required && (
              <span className="text-[10px] font-semibold tracking-wide uppercase text-[#B60F2A] bg-[#FBE7EA] px-1.5 py-0.5 rounded">
                Required
              </span>
            )}
            {doc.multi && (
              <span className="text-[10px] font-semibold tracking-wide uppercase text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                Multi
              </span>
            )}
          </div>
          <p className="text-xs text-[#6B7280] mt-1">{doc.description}</p>
        </div>
        <div className="shrink-0">
          {needsReupload ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-800 bg-orange-100 ring-1 ring-inset ring-orange-300 px-2 py-1 rounded-full" data-testid={`upload-badge-reupload-${doc.id}`}>
              <Clock className="h-3.5 w-3.5" /> Reupload requested
            </span>
          ) : hasFiles ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-1 rounded-full">
              <CheckCircle2 className="h-3.5 w-3.5" /> Uploaded
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 px-2 py-1 rounded-full">
              <Clock className="h-3.5 w-3.5" /> Pending
            </span>
          )}
        </div>
      </div>

      {needsReupload && (
        <div className="rounded-xl bg-orange-50 border border-orange-200 p-3 text-xs text-orange-900" data-testid={`upload-reupload-banner-${doc.id}`}>
          <div className="font-semibold flex items-center gap-1"><Clock className="h-3 w-3" /> Admin requested a fresh copy of {doc.label}</div>
          {(reuploadReason || state.lastActionReason) && <div className="mt-1"><span className="font-semibold">Reason:</span> {reuploadReason || state.lastActionReason}</div>}
          <div className="mt-1 text-orange-800">Choose a new file below, double-check it&rsquo;s correct, then click Upload.</div>
        </div>
      )}

      {!hasPending && (
        <div
          role="button"
          tabIndex={0}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => (locked ? onLockedClick?.() : inputRef.current?.click())}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (locked ? onLockedClick?.() : inputRef.current?.click())}
          className={`border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center text-center transition-all ${
            locked
              ? "cursor-not-allowed border-[#E5E7EB]"
              : dragOver
              ? "cursor-pointer border-[#B60F2A] bg-[#FBE7EA]/60"
              : "cursor-pointer border-[#E5E7EB] hover:border-[#B60F2A] hover:bg-[#FBE7EA]/40"
          }`}
          data-testid={`upload-zone-${doc.id}`}
        >
          <UploadCloud className="h-7 w-7 text-[#9CA3AF] mb-2" />
          <div className="text-sm text-[#111] font-medium">
            {hasFiles && doc.multi ? "Drop or click to add more" : "Drop file here or click to browse"}
          </div>
          <div className="text-xs text-[#9CA3AF] mt-1">{doc.accept === ".pdf" ? "PDF only" : "PDF, JPG, PNG"} · Max 5 MB</div>
        </div>
      )}
      {/* type="file" inputs must stay in the layout (not display:none) or some
          mobile browsers (iOS Safari in particular) silently refuse to open
          the native picker when triggered via a proxied .click() call. */}
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        multiple={doc.multi}
        accept={doc.accept || ".pdf,image/*"}
        onChange={(e) => { stageFiles(e.target.files); e.target.value = ""; }}
        data-testid={`upload-input-${doc.id}`}
      />

      {hasPending && (
        <div className="rounded-xl border-2 border-dashed border-[#B60F2A] bg-[#FBE7EA]/30 p-4 space-y-3" data-testid={`upload-pending-${doc.id}`}>
          <div className="text-xs font-semibold text-[#B60F2A] uppercase tracking-wide">Review before uploading</div>
          <ul className="space-y-2">
            {pending.map((f, idx) => {
              const isImage = f.dataUrl?.startsWith("data:image");
              return (
                <li key={`${f.name}-${idx}`} className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E7EB] bg-white" data-testid={`upload-pending-file-${doc.id}-${idx}`}>
                  <button
                    type="button"
                    onClick={() => setPendingPreviewIdx(idx)}
                    className="h-10 w-10 rounded-lg bg-[#F7F7F7] border border-[#E5E7EB] flex items-center justify-center overflow-hidden shrink-0 hover:border-[#B60F2A] transition"
                    aria-label="Preview file"
                  >
                    {isImage ? (
                      <img src={f.dataUrl} alt={f.name} className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="h-4 w-4 text-[#B60F2A]" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#111] truncate">{f.name}</div>
                    <div className="text-xs text-[#9CA3AF]">{formatSize(f.size)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingPreviewIdx(idx)}
                    className="p-2 rounded-lg text-[#6B7280] hover:text-[#B60F2A] hover:bg-[#F7F7F7] transition-colors"
                    aria-label="Preview file"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removePending(idx)}
                    className="p-2 rounded-lg text-[#6B7280] hover:text-red-600 hover:bg-[#F7F7F7] transition-colors"
                    aria-label="Discard file"
                    data-testid={`upload-pending-discard-${doc.id}-${idx}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={confirmUpload}
              disabled={uploading}
              className="mu-btn-primary flex-1 justify-center text-sm"
              data-testid={`upload-confirm-${doc.id}`}
            >
              <Send className="h-4 w-4" /> {uploading ? "Uploading…" : `Upload ${pending.length > 1 ? `${pending.length} files` : "file"}`}
            </button>
            {doc.multi && (
              <button
                type="button"
                onClick={() => (locked ? onLockedClick?.() : inputRef.current?.click())}
                className="mu-btn-secondary text-sm"
                data-testid={`upload-pending-add-more-${doc.id}`}
              >
                <Plus className="h-4 w-4" /> Add more
              </button>
            )}
            <button
              type="button"
              onClick={() => setPending([])}
              disabled={uploading}
              className="mu-btn-secondary text-sm"
              data-testid={`upload-cancel-${doc.id}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {uploading && (
        <div className="w-full h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden">
          <div
            className="h-full bg-[#B60F2A] transition-all duration-200"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      <AnimatePresence initial={false}>
        {hasFiles && (
          <motion.ul
            layout
            className="space-y-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {files.map((f, idx) => {
              const thumbSrc = f.file_url || f.dataUrl;
              const isImage =
                thumbSrc?.startsWith("data:image") ||
                /\.(png|jpe?g|webp|gif)$/i.test(f.name || "");
              return (
              <motion.li
                key={`${f.name}-${idx}`}
                layout
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7]"
                data-testid={`upload-file-${doc.id}-${idx}`}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewIdx(idx);
                  }}
                  className="h-10 w-10 rounded-lg bg-white border border-[#E5E7EB] flex items-center justify-center overflow-hidden shrink-0 hover:border-[#B60F2A] transition"
                  aria-label="Preview file"
                  data-testid={`upload-thumb-${doc.id}-${idx}`}
                >
                  {isImage && thumbSrc ? (
                    <img src={thumbSrc} alt={f.name} className="w-full h-full object-cover" />
                  ) : (
                    <FileText className="h-4 w-4 text-[#B60F2A]" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#111] truncate">{f.name}</div>
                  <div className="text-xs text-[#9CA3AF]">{formatSize(f.size)}</div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewIdx(idx);
                  }}
                  className="p-2 rounded-lg text-[#6B7280] hover:text-[#B60F2A] hover:bg-white transition-colors"
                  aria-label="Preview file"
                  data-testid={`upload-preview-${doc.id}-${idx}`}
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(idx);
                  }}
                  className="p-2 rounded-lg text-[#6B7280] hover:text-[#B60F2A] hover:bg-white transition-colors"
                  data-testid={`upload-remove-${doc.id}-${idx}`}
                  aria-label="Remove file"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </motion.li>
            );})}
            {doc.multi && !hasPending && (
              <button
                type="button"
                onClick={() => (locked ? onLockedClick?.() : inputRef.current?.click())}
                className="w-full inline-flex items-center justify-center gap-2 text-xs font-medium text-[#B60F2A] hover:bg-[#FBE7EA]/60 rounded-lg py-2 border border-dashed border-[#FBE7EA]"
                data-testid={`upload-add-more-${doc.id}`}
              >
                <Plus className="h-3.5 w-3.5" /> Add another receipt
              </button>
            )}
          </motion.ul>
        )}
      </AnimatePresence>

      <FilePreviewDialog
        open={previewIdx !== null}
        onOpenChange={(v) => !v && setPreviewIdx(null)}
        file={previewIdx !== null ? files[previewIdx] : null}
        docLabel={doc.label}
      />
      <FilePreviewDialog
        open={pendingPreviewIdx !== null}
        onOpenChange={(v) => !v && setPendingPreviewIdx(null)}
        file={pendingPreviewIdx !== null ? pending[pendingPreviewIdx] : null}
        docLabel={doc.label}
      />
    </motion.div>
  );
};
